import React from 'react';
import { motion } from 'framer-motion';
import { BuildingOffice2Icon, AcademicCapIcon, GlobeAltIcon } from '@heroicons/react/24/outline';

const InvSlide12Market: React.FC = () => {
  const stats = [
    {
      icon: AcademicCapIcon,
      value: '4.1M',
      label: 'Students in Australia',
      color: '#0ea5e9',
    },
    {
      icon: BuildingOffice2Icon,
      value: '~3,000',
      label: 'Private Schools',
      color: '#10b981',
    },
    {
      icon: BuildingOffice2Icon,
      value: '~6,700',
      label: 'Public Schools',
      color: '#f59e0b',
    },
  ];

  return (
    <div className="flex flex-col items-center justify-center h-full px-16">
      <motion.h2
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-5xl font-bold text-white mb-3 text-center"
      >
        Market Opportunity
      </motion.h2>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-xl text-[#94a3b8] mb-10 text-center"
      >
        Large addressable market with exceptional customer lifetime value
      </motion.p>

      {/* Key Stats */}
      <div className="flex gap-10 mb-10">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 + index * 0.1 }}
            className="text-center"
          >
            <div
              className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{
                background: `${stat.color}20`,
                border: `2px solid ${stat.color}50`,
              }}
            >
              <stat.icon className="w-10 h-10" style={{ color: stat.color }} />
            </div>
            <div className="text-5xl font-bold text-white mb-1">{stat.value}</div>
            <div className="text-[#94a3b8]">{stat.label}</div>
          </motion.div>
        ))}
      </div>

      <div className="flex gap-8 w-full max-w-5xl">
        {/* Left: Retention */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6 }}
          className="flex-1"
        >
          <div
            className="p-6 rounded-xl h-full"
            style={{
              background: 'rgba(16, 185, 129, 0.1)',
              border: '2px solid rgba(16, 185, 129, 0.4)',
            }}
          >
            <h3 className="text-2xl font-semibold text-[#10b981] mb-4">Exceptional Retention</h3>

            <div className="space-y-4 text-[#94a3b8]">
              <p>
                Unlike most SaaS, students don't churn — they <span className="text-white font-bold">graduate to the next year level</span>.
              </p>

              <div className="flex items-center gap-4 p-4 rounded-lg bg-[#10b981]/10">
                <div className="text-5xl font-bold text-[#10b981]">13</div>
                <div>
                  <div className="text-white font-semibold">Year Customer Lifetime</div>
                  <div className="text-sm">Kindergarten to Year 12</div>
                </div>
              </div>

              <p>
                Profile follows students from school to school. Population isn't decreasing — it's growing.
              </p>

              <p className="font-medium text-white">
                "Repeat customers, as I'd like to say."
              </p>
            </div>
          </div>
        </motion.div>

        {/* Right: Expansion */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.7 }}
          className="flex-1"
        >
          <div
            className="p-6 rounded-xl h-full"
            style={{
              background: 'rgba(14, 165, 233, 0.1)',
              border: '2px solid rgba(14, 165, 233, 0.4)',
            }}
          >
            <h3 className="text-2xl font-semibold text-[#0ea5e9] mb-4 flex items-center gap-2">
              <GlobeAltIcon className="w-7 h-7" />
              Global Potential
            </h3>

            <div className="space-y-4 text-[#94a3b8]">
              <p>System designed for internationalization from day one:</p>

              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-[#0ea5e9]">•</span>
                  <span>Dynamic labels adapt to any country</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#0ea5e9]">•</span>
                  <span>Mastery system works with any curriculum</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#0ea5e9]">•</span>
                  <span>AI generates localized content</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#0ea5e9]">•</span>
                  <span>Content marketplace for regional subjects</span>
                </li>
              </ul>

              <div
                className="p-3 rounded-lg text-center"
                style={{ background: 'rgba(14, 165, 233, 0.2)' }}
              >
                <p className="text-white">
                  Australia first, then <span className="font-bold text-[#0ea5e9]">UK, Canada, US, Middle East, Asia</span>
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default InvSlide12Market;
